import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useState } from "react";
import { dateLocale } from "@/lib/dateTime";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRightLeft,
  Ban,
  BarChart3,
  Boxes,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock,
  FileText,
  Flame,
  Hourglass,
  Inbox,
  IndianRupee,
  Layers,
  MapPin,
  ListChecks,
  Loader2,
  Package,
  Percent,
  Phone,
  Scale,
  Send,
  Shuffle,
  Table2,
  Target,
  Timer,
  TriangleAlert,
  Trophy,
  UserCheck,
  UserX,
  Users,
  XCircle,
} from "lucide-react";
import {
  BarList,
  ColumnChart,
  DataTable,
  EmptyState,
  ReportSkeleton,
  ProgressBar,
  SectionCard,
  StackLegend,
  StackedBar,
  StatTile,
  StatusPill,
  TONE,
  type Tone,
  achievementTone,
  fmtDate,
  fmtMoney,
  fmtMonth,
  fmtNum,
  fmtPct,
  type Column,
} from "@/components/reports/ReportWidgets";
import { buildQuery, filterSelectClass, useReport, type DateRange } from "@/components/reports/DateRangeFilter";
import { cn } from "@/lib/utils";
import { ListPager } from "@/components/ui/list-pager";
import type { ExecRow, ForecastBucket, HeadRow, LeadSourceReport, PipelineOwnerRow, PipelineSummary, StageRow, UserMetrics } from "@/types/report";

export interface TabProps {
  range: DateRange;
  headId: string;
  /** Salesperson filter */
  userId?: string;
  /** The report's own filters as API parameters (pipeline_id, lead_source, product_id, quote_status …) */
  extra?: Record<string, string>;
}

function Status({ loading, error, children }: { loading: boolean; error: string | null; children: React.ReactNode }) {
  if (error) return <p className="text-sm text-destructive py-6 text-center">{error}</p>;
  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-10 bg-background/40">
          <Loader2 className="size-5 animate-spin text-primary" />
        </div>
      )}
      {children}
    </div>
  );
}

const q = (range: DateRange, headId: string, userId = "", extra: Record<string, string | number> = {}) =>
  buildQuery({ from: range.from, to: range.to, head_id: headId, user_id: userId, ...extra });

// ---------------------------------------------------------------------------
// Sales Executives
// ---------------------------------------------------------------------------

/** A count that needs action: orange (or red) when there is any, muted when none */
function Flag({ n, tone = "orange" }: { n: number; tone?: Tone }) {
  return n ? <span className={cn("font-semibold", TONE[tone].text)}>{fmtNum(n)}</span> : <span className="text-muted-foreground">0</span>;
}

export function ExecTab({ range, headId, userId }: TabProps) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<{ rows: ExecRow[]; totals: UserMetrics }>(`/api/Report/ExecPerformance${q(range, headId, userId)}`);
  if (!data && !error) return <ReportSkeleton tiles={8} cards={1} />;
  const rows = data?.rows || [];

  const columns: Column<ExecRow>[] = [
    { key: "name", header: "Executive", render: (r) => <span className="font-medium text-foreground">{r.name}{!r.active && <span className="text-muted-foreground"> (inactive)</span>}</span> },
    { key: "sales_head", header: "Sales Head", render: (r) => r.sales_head || "—" },
    { key: "leads_assigned", header: "Leads", align: "right" },
    { key: "leads_converted", header: "Converted", align: "right", render: (r) => <span className="text-success">{r.leads_converted}</span> },
    { key: "conversion_pct", header: "Conv. %", align: "right", render: (r) => fmtPct(r.conversion_pct) },
    { key: "leads_untouched", header: "Untouched", align: "right", render: (r) => <Flag n={r.leads_untouched} /> },
    { key: "deals_won", header: "Won", align: "right", render: (r) => <span className="text-success">{r.deals_won}</span> },
    { key: "deals_lost", header: "Lost", align: "right", render: (r) => <Flag n={r.deals_lost} tone="red" /> },
    { key: "won_amount", header: "Revenue", align: "right", render: (r) => <span className="font-semibold text-success">{fmtMoney(r.won_amount)}</span> },
    { key: "win_rate_pct", header: "Win rate", align: "right", render: (r) => fmtPct(r.win_rate_pct) },
    { key: "avg_deal_size", header: "Avg deal", align: "right", render: (r) => fmtMoney(r.avg_deal_size) },
    { key: "avg_cycle_days", header: "Cycle (days)", align: "right" },
    { key: "pipeline_amount", header: "Pipeline", align: "right", render: (r) => <span className="text-info">{fmtMoney(r.pipeline_amount)}</span> },
    { key: "quotes_sent", header: "Quotes sent", align: "right" },
    { key: "quotes_accepted", header: "Accepted", align: "right" },
    { key: "avg_discount_pct", header: "Avg disc.", align: "right", render: (r) => fmtPct(r.avg_discount_pct) },
    { key: "calls", header: "Calls", align: "right" },
    { key: "meetings", header: "Meetings", align: "right" },
    { key: "tasks_done", header: "Tasks done", align: "right" },
    { key: "tasks_overdue", header: "Overdue", align: "right", render: (r) => <Flag n={r.tasks_overdue} tone="red" /> },
    {
      key: "target_achieved_pct",
      header: "Target",
      sortValue: (r) => r.target_achieved_pct ?? -1,
      render: (r) => (r.target_amount ? <ProgressBar pct={r.target_achieved_pct} className="min-w-[120px]" /> : <span className="text-xs text-muted-foreground">No target</span>),
    },
  ];

  const t = data?.totals;
  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        {t && (
          <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
            <StatTile label="Revenue won" value={fmtMoney(t.won_amount)} tone="green" icon={<Trophy />} hint={<span>{t.deals_won} deal{t.deals_won === 1 ? "" : "s"} won</span>} />
            <StatTile
              label="Target achieved"
              value={fmtPct(t.target_achieved_pct)}
              tone={achievementTone(t.target_achieved_pct)}
              icon={<Target />}
              hint={<span>{t.target_amount ? `of ${fmtMoney(t.target_amount)}` : "no target set"}</span>}
            />
            <StatTile label="Lead conversion" value={fmtPct(t.conversion_pct)} tone="purple" icon={<Percent />} hint={<span>{t.leads_converted} of {t.leads_assigned} leads</span>} />
            <StatTile label="Win rate" value={fmtPct(t.win_rate_pct)} tone="blue" icon={<Scale />} hint={<span>{t.deals_won} won · {t.deals_lost} lost</span>} />
            <StatTile label="Open pipeline" value={fmtMoney(t.pipeline_amount)} tone="blue" icon={<Layers />} hint={<span>{t.open_deals} deals · as of now</span>} />
            <StatTile label="Activities" value={fmtNum(t.calls + t.meetings + t.emails + t.whatsapp + t.notes)} tone="blue" icon={<ListChecks />} hint={<span>{t.calls} calls · {t.meetings} meetings</span>} />
            <StatTile label="Untouched leads" value={fmtNum(t.leads_untouched)} tone={t.leads_untouched ? "orange" : "green"} icon={<Hourglass />} hint={<span>new and never contacted</span>} />
            <StatTile label="Overdue tasks" value={fmtNum(t.tasks_overdue)} tone={t.tasks_overdue ? "red" : "green"} icon={<TriangleAlert />} hint={<span>right now</span>} />
          </div>
        )}
        {rows.length > 1 && (
          <SectionCard title="Revenue by executive" description="Won in the period" icon={<BarChart3 />} tone="green">
            <BarList tone="green" data={rows.map((r) => ({ label: r.name, value: r.won_amount, detail: [`${r.deals_won} deals won`] }))} format={(v) => fmtMoney(v)} emptyMessage="Nothing won in this period." />
          </SectionCard>
        )}
        <SectionCard title="Sales Executive performance" description="Click a row to see that executive's leads, deals and activity. Pipeline and overdue tasks are as of now." icon={<Users />}>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.user_id}
            onRowClick={(r) => navigate(`/reports/user/${r.user_id}`)}
            defaultSort={{ key: "won_amount", dir: "desc" }}
            emptyMessage="No Sales Executives in this view."
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Sales Heads (Super Admin)
// ---------------------------------------------------------------------------

export function HeadTab({ range, onPickHead }: TabProps & { onPickHead: (id: string) => void }) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<{ rows: HeadRow[] }>(`/api/Report/HeadPerformance${q(range, "")}`);
  if (!data && !error) return <ReportSkeleton tiles={6} cards={1} />;
  const rows = data?.rows || [];
  const t = (k: keyof HeadRow) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const won = t("won_amount");
  const target = t("target_amount");
  // Achievement compares like with like: revenue of the teams that have a target, against those targets
  const wonWithTarget = rows.reduce((s, r) => s + (r.target_amount ? r.won_amount : 0), 0);
  const achieved = target ? Math.round((wonWithTarget / target) * 1000) / 10 : null;

  const columns: Column<HeadRow>[] = [
    { key: "name", header: "Sales Head", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "team_size", header: "Executives", align: "right" },
    { key: "leads_assigned", header: "Leads", align: "right" },
    { key: "conversion_pct", header: "Conv. %", align: "right", render: (r) => fmtPct(r.conversion_pct) },
    { key: "won_amount", header: "Revenue", align: "right", render: (r) => <span className="font-semibold text-success">{fmtMoney(r.won_amount)}</span> },
    { key: "win_rate_pct", header: "Win rate", align: "right", render: (r) => fmtPct(r.win_rate_pct) },
    { key: "pipeline_amount", header: "Pipeline", align: "right", render: (r) => <span className="text-info">{fmtMoney(r.pipeline_amount)}</span> },
    { key: "quotes_pending_approval", header: "Pending approvals", align: "right", render: (r) => (r.quotes_pending_approval ? <StatusPill tone="orange">{r.quotes_pending_approval}</StatusPill> : <span className="text-muted-foreground">0</span>) },
    { key: "avg_approval_hours", header: "Avg approval (h)", align: "right" },
    { key: "tasks_overdue", header: "Overdue tasks", align: "right", render: (r) => <Flag n={r.tasks_overdue} tone="red" /> },
    {
      key: "target_achieved_pct",
      header: "Team target",
      sortValue: (r) => r.target_achieved_pct ?? -1,
      render: (r) => (r.target_amount ? <ProgressBar pct={r.target_achieved_pct} className="min-w-[120px]" /> : <span className="text-xs text-muted-foreground">No target</span>),
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <span className="flex gap-3">
          <button type="button" className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); onPickHead(r.user_id); }}>
            Team
          </button>
          <button type="button" className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/reports/user/${r.user_id}`); }}>
            Details
          </button>
        </span>
      ),
    },
  ];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-3 @6xl/main:grid-cols-6">
          <StatTile label="Teams" value={fmtNum(rows.length)} tone="blue" icon={<Users />} hint={<span>{fmtNum(t("team_size"))} executives</span>} />
          <StatTile label="Revenue won" value={fmtMoney(won)} tone="green" icon={<Trophy />} hint={<span>{fmtNum(t("deals_won"))} deals</span>} />
          <StatTile label="Team targets" value={target ? fmtMoney(target) : "—"} tone={achievementTone(achieved)} icon={<Target />} hint={<span>{achieved === null ? "no targets set" : `${fmtPct(achieved)} achieved`}</span>} />
          <StatTile label="Open pipeline" value={fmtMoney(t("pipeline_amount"))} tone="blue" icon={<Layers />} hint={<span>as of now</span>} />
          <StatTile label="Waiting for approval" value={fmtNum(t("quotes_pending_approval"))} tone={t("quotes_pending_approval") ? "orange" : "green"} icon={<Clock />} hint={<span>quotes, right now</span>} />
          <StatTile label="Overdue tasks" value={fmtNum(t("tasks_overdue"))} tone={t("tasks_overdue") ? "red" : "green"} icon={<TriangleAlert />} hint={<span>right now</span>} />
        </div>
        {rows.length > 1 && (
          <SectionCard title="Revenue by team" description="Won in the period" icon={<BarChart3 />} tone="green">
            <BarList tone="green" data={rows.map((r) => ({ label: r.name, value: r.won_amount, detail: [`${r.team_size} executives`] }))} format={(v) => fmtMoney(v)} emptyMessage="Nothing won in this period." />
          </SectionCard>
        )}
        <SectionCard title="Sales Head performance" description="Totals for each head and everyone reporting to them. Team target = the head's own target, or the sum of their executives' targets." icon={<Users />} tone="purple">
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.user_id} onRowClick={(r) => onPickHead(r.user_id)} defaultSort={{ key: "won_amount", dir: "desc" }} emptyMessage="No Sales Heads yet." />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Pipeline
// ---------------------------------------------------------------------------

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: React.ReactNode; tone?: "success" | "warning" }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-border bg-card px-3.5 py-2.5">
      <span className="text-[11.5px] text-muted-foreground">{label}</span>
      <span className="font-mono text-xl font-semibold">{value}</span>
      <span className={tone === "success" ? "text-[11.5px] text-success" : tone === "warning" ? "text-[11.5px] text-warning" : "text-[11.5px] text-muted-foreground"}>{sub}</span>
    </div>
  );
}

/** "▲ 12% vs last period" or "▼ 4 days slower" */
function change(now: number, before: number, unit: "pct" | "days") {
  if (unit === "days") {
    const d = now - before;
    return d === 0 ? "same as last period" : `${d > 0 ? "▲" : "▼"} ${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} ${d > 0 ? "slower" : "faster"}`;
  }
  if (before === 0) return now === 0 ? "nothing in either period" : "nothing the period before";
  const p = Math.round(((now - before) / before) * 100);
  return `${p >= 0 ? "▲" : "▼"} ${Math.abs(p)}% vs the period before`;
}

export function PipelineTab({ range, headId, userId, extra }: TabProps) {
  const { data, loading, error } = useReport<{
    stages: StageRow[];
    ageing: Array<{ bucket: string; deals: number; amount: number }>;
    stale_deals: Array<{ id: string; name: string; amount: number; stage_name: string; owner_name: string; idle_days: number }>;
    summary: PipelineSummary;
    monthly_won: Array<{ period: string; amount: number; deals: number }>;
    owners: PipelineOwnerRow[];
  }>(`/api/Report/Pipeline${q(range, headId, userId, extra || {})}`);
  if (!data && !error) return <ReportSkeleton tiles={4} cards={2} />;

  const sm = data?.summary;
  const stages = data?.stages || [];
  const maxAmount = Math.max(1, ...stages.map((s) => s.amount));
  const slowest = [...stages].filter((s) => (s.avg_days ?? 0) > 0).sort((a, b) => (b.avg_days ?? 0) - (a.avg_days ?? 0))[0];
  const owners = data?.owners || [];
  const stalled = owners.reduce((t, o) => t + o.stalled_deals, 0);
  const slower = sm && sm.cycle_days !== null && sm.prev_cycle_days !== null && sm.cycle_days > sm.prev_cycle_days;

  const ownerColumns: Column<PipelineOwnerRow>[] = [
    { key: "name", header: "Owner", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "open_deals", header: "Open deals", align: "right" },
    { key: "open_amount", header: "Open value", align: "right", render: (r) => fmtMoney(r.open_amount) },
    { key: "weighted", header: "Weighted", align: "right", render: (r) => <span className="text-purple">{fmtMoney(r.weighted)}</span> },
    { key: "won_amount", header: "Won", align: "right", render: (r) => <span className="font-semibold text-success">{fmtMoney(r.won_amount)}</span> },
    { key: "lost_deals", header: "Lost", align: "right", render: (r) => <span className={r.lost_deals ? "text-danger" : ""}>{r.lost_deals}</span> },
    { key: "win_rate_pct", header: "Win rate", align: "right", sortValue: (r) => r.win_rate_pct ?? -1, render: (r) => fmtPct(r.win_rate_pct) },
    {
      key: "stalled_deals",
      header: "Stalled 14+ days",
      render: (r) => (r.stalled_deals ? <StatusPill tone="orange">{r.stalled_deals} deal{r.stalled_deals === 1 ? "" : "s"} · {fmtMoney(r.stalled_amount)}</StatusPill> : <span className="text-muted-foreground">—</span>),
    },
  ];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        {sm && (
          <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-5">
            <StatTile label="Open pipeline" value={fmtMoney(sm.open_amount)} tone="blue" icon={<Layers />} hint={<span>{sm.open_deals} open deal{sm.open_deals === 1 ? "" : "s"} · as of now</span>} />
            <StatTile label="Weighted" value={fmtMoney(sm.weighted)} tone="purple" icon={<Scale />} hint={<span>by stage probability</span>} />
            <StatTile
              label="Won in the period"
              value={fmtMoney(sm.won_amount)}
              tone={sm.won_amount >= sm.prev_won_amount ? "green" : "red"}
              icon={<Trophy />}
              rawValue={sm.won_amount}
              previous={sm.prev_won_amount}
              previousLabel="vs the period before"
              hint={<span>· {sm.won_deals} deal{sm.won_deals === 1 ? "" : "s"}</span>}
            />
            <StatTile
              label="Average cycle"
              value={sm.cycle_days === null ? "—" : `${sm.cycle_days} days`}
              tone={slower ? "orange" : "blue"}
              icon={<Timer />}
              hint={<span>{sm.cycle_days === null ? "no wins in the period" : sm.prev_cycle_days === null ? "none to compare with" : change(sm.cycle_days, sm.prev_cycle_days, "days")}</span>}
            />
            <StatTile label="Stalled deals" value={fmtNum(stalled)} tone={stalled ? "orange" : "green"} icon={<TriangleAlert />} hint={<span>no activity for 14+ days</span>} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-2">
          <SectionCard title="Open value by stage" description="Bar = value · then deals and the average days they have sat in the stage (as of now)" icon={<Layers />}>
            {stages.length === 0 ? (
              <EmptyState message="No open deals." />
            ) : (
              <div className="space-y-2.5">
                {stages.map((s) => (
                  <div key={s.stage_id} className="grid grid-cols-[110px_minmax(0,1fr)_170px] items-center gap-3 text-xs">
                    <span className="truncate text-foreground/80" title={s.stage_name}>{s.stage_name}</span>
                    <div className="h-3.5 rounded-sm" style={{ background: "var(--viz-track)" }} role="img" aria-label={`${s.stage_name}: ${fmtMoney(s.amount)}`}>
                      <div className="h-full rounded-sm bg-info" style={{ width: `${Math.max(2, (s.amount / maxAmount) * 100)}%` }} />
                    </div>
                    <span className="flex justify-between gap-2">
                      <span className="font-mono font-semibold">{fmtMoney(s.amount)}</span>
                      <span className="text-muted-foreground">{s.deals} deal{s.deals === 1 ? "" : "s"}</span>
                      <span className={slowest && s.stage_id === slowest.stage_id ? "font-semibold text-warning" : "text-muted-foreground"}>{s.avg_days ? `${s.avg_days} d` : "—"}</span>
                    </span>
                  </div>
                ))}
                {slowest && <p className="pt-1 text-xs font-medium text-warning">Deals wait longest in {slowest.stage_name}: {slowest.avg_days} days on average.</p>}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Won value by month" description="The last twelve months" icon={<BarChart3 />} tone="green">
            <ColumnChart data={(data?.monthly_won || []).map((m) => ({ label: fmtMonth(m.period), value: m.amount, detail: [`${m.deals} deal${m.deals === 1 ? "" : "s"}`] }))} format={(v) => fmtMoney(v)} emptyMessage="Nothing won in the last twelve months." />
          </SectionCard>
        </div>

        <SectionCard title="By owner" description="Open figures are as of now; won, lost and win rate follow the period above" icon={<Users />}>
          <DataTable columns={ownerColumns} rows={owners} rowKey={(r) => r.user_id} defaultSort={{ key: "won_amount", dir: "desc" }} emptyMessage="No deals for anyone in this view." />
        </SectionCard>

        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-2">
          <SectionCard title="Deal age" description="Open deals by days since they were created" icon={<Hourglass />} tone="purple">
            <BarList tone="purple" data={(data?.ageing || []).map((a) => ({ label: a.bucket, value: a.deals, detail: [fmtMoney(a.amount)] }))} emptyMessage="No open deals." />
          </SectionCard>
          <SectionCard title="Stale deals" description="Open deals with no activity for 14+ days" icon={<TriangleAlert />} tone="orange">
            <DataTable
              columns={[
                { key: "name", header: "Deal", render: (r) => <Link to={`/deals/${r.id}`} className="font-medium text-primary hover:underline">{r.name}</Link> },
                { key: "stage_name", header: "Stage" },
                { key: "owner_name", header: "Owner" },
                { key: "amount", header: "Amount", align: "right", render: (r) => fmtMoney(r.amount) },
                { key: "idle_days", header: "Idle days", align: "right", render: (r) => <span className={r.idle_days > 30 ? "font-semibold text-danger" : "text-warning"}>{r.idle_days}</span> },
              ]}
              rows={data?.stale_deals || []}
              rowKey={(r) => r.id}
              defaultSort={{ key: "idle_days", dir: "desc" }}
              emptyMessage="No stale deals."
            />
          </SectionCard>
        </div>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Lead sources
// ---------------------------------------------------------------------------

const LEAD_STATUS_LABEL: Record<string, string> = { NEW: "New", CONTACTED: "Contacted", FOLLOW_UP: "Follow-up", QUALIFIED: "Qualified", CONVERTED: "Converted", DISQUALIFIED: "Disqualified" };
const PRIORITY_TONES: { key: string; label: string; tone: Tone; note: string }[] = [
  { key: "HOT", label: "Hot", tone: "red", note: "Ready to buy" },
  { key: "WARM", label: "Warm", tone: "orange", note: "Interested" },
  { key: "COLD", label: "Cold", tone: "blue", note: "Only exploring" },
];

export function SourcesTab({ range, headId, userId, leadStatus, leadSource, priority }: TabProps & { leadStatus?: string; leadSource?: string; priority?: string }) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<LeadSourceReport>(
    `/api/Report/LeadSource${q(range, headId, userId, { lead_status: leadStatus || "", lead_source: leadSource || "", priority: priority || "" })}`,
  );
  if (!data && !error) return <ReportSkeleton tiles={4} cards={2} />;
  const rows = data?.rows || [];
  const owners = data?.by_owner || [];
  const leads = rows.reduce((s, r) => s + r.leads, 0);
  const converted = rows.reduce((s, r) => s + r.converted, 0);
  const disqualified = rows.reduce((s, r) => s + r.disqualified, 0);
  const won = rows.reduce((s, r) => s + r.won_amount, 0);
  const conv = leads ? Math.round((converted / leads) * 1000) / 10 : 0;
  const prio = (k: string) => data?.by_priority.find((x) => x.key === k)?.leads || 0;

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
          <StatTile label="Leads" value={fmtNum(leads)} tone="blue" icon={<Users />} hint={<span>Created in the period</span>} />
          <StatTile label="Converted" value={fmtNum(converted)} tone="green" icon={<CheckCircle2 />} hint={<span>{fmtPct(conv)} conversion rate</span>} />
          <StatTile label="Disqualified" value={fmtNum(disqualified)} tone={disqualified ? "red" : "gray"} icon={<XCircle />} hint={<span>{leads ? fmtPct(Math.round((disqualified / leads) * 1000) / 10) : "—"} of leads</span>} />
          <StatTile label="Revenue from these leads" value={fmtMoney(won)} tone="purple" icon={<IndianRupee />} hint={<span>Won deals that came from them</span>} />
        </div>

        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-3">
          <SectionCard title="Leads by source" description="Leads created in the period" icon={<Inbox />} className="@4xl/main:col-span-2">
            <BarList data={rows.map((r) => ({ label: r.source_name, value: r.leads, detail: [`${r.converted} converted · ${fmtPct(r.conversion_pct)}`] }))} emptyMessage="No leads for these filters." />
          </SectionCard>
          <SectionCard title="Priority" description="How ready the leads are" icon={<Flame />} tone="red">
            <div className="space-y-3">
              {PRIORITY_TONES.map((p) => {
                const n = prio(p.key);
                return (
                  <div key={p.key} className="space-y-1">
                    <div className="flex items-baseline justify-between text-xs">
                      <StatusPill tone={p.tone}>{p.label}</StatusPill>
                      <span className="font-mono font-semibold">
                        {fmtNum(n)} <span className="font-normal text-muted-foreground">· {leads ? Math.round((n / leads) * 100) : 0}%</span>
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full" style={{ background: "var(--viz-track)" }}>
                      <div className={TONE[p.tone].bar + " h-full rounded-full"} style={{ width: `${leads ? (n / leads) * 100 : 0}%` }} />
                    </div>
                    <span className="text-[11px] text-muted-foreground">{p.note}</span>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </div>

        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-2">
          <SectionCard title="Leads by status" description="Where these leads are now" icon={<ListChecks />} tone="purple">
            <BarList tone="purple" data={(data?.by_status || []).map((r) => ({ label: LEAD_STATUS_LABEL[r.key] || r.key, value: r.leads }))} emptyMessage="No leads for these filters." />
          </SectionCard>
          <SectionCard title="Revenue by source" description="Won deals that came from these leads" icon={<IndianRupee />} tone="green">
            <BarList tone="green" data={rows.filter((r) => r.won_amount > 0).map((r) => ({ label: r.source_name, value: r.won_amount }))} format={(v) => fmtMoney(v)} emptyMessage="No revenue from these leads yet." />
          </SectionCard>
        </div>

        <SectionCard title="Source details" description="Every source, with conversion and revenue" icon={<Table2 />}>
          <DataTable
            columns={[
              { key: "source_name", header: "Source", render: (r) => <span className="font-medium text-foreground">{r.source_name}</span> },
              { key: "leads", header: "Leads", align: "right" },
              { key: "converted", header: "Converted", align: "right", render: (r) => <span className="text-success">{r.converted}</span> },
              { key: "disqualified", header: "Disqualified", align: "right", render: (r) => <span className={r.disqualified ? "text-danger" : ""}>{r.disqualified}</span> },
              { key: "conversion_pct", header: "Conversion", align: "right", render: (r) => fmtPct(r.conversion_pct) },
              { key: "won_amount", header: "Revenue", align: "right", render: (r) => fmtMoney(r.won_amount) },
            ]}
            rows={rows}
            rowKey={(r) => r.source}
            defaultSort={{ key: "leads", dir: "desc" }}
            emptyMessage="No leads for these filters."
            footer={
              rows.length > 1 ? (
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold">
                    <td className="px-2 py-2">Total</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtNum(leads)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtNum(converted)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtNum(disqualified)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtPct(conv)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(won)}</td>
                  </tr>
                </tfoot>
              ) : undefined
            }
          />
        </SectionCard>

        {owners.length > 1 && (
          <SectionCard title="Leads by salesperson" description="Click a row for that person's report" icon={<Users />}>
            <DataTable
              columns={[
                { key: "name", header: "Salesperson", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
                { key: "leads", header: "Leads", align: "right" },
                { key: "hot", header: "Hot", align: "right", render: (r) => <span className="text-danger">{r.hot}</span> },
                { key: "warm", header: "Warm", align: "right", render: (r) => <span className="text-warning">{r.warm}</span> },
                { key: "cold", header: "Cold", align: "right", render: (r) => <span className="text-info">{r.cold}</span> },
                { key: "working", header: "Being worked", align: "right" },
                { key: "converted", header: "Converted", align: "right" },
                { key: "disqualified", header: "Disqualified", align: "right" },
                { key: "conversion_pct", header: "Conversion", align: "right", render: (r) => fmtPct(r.conversion_pct) },
              ]}
              rows={owners}
              rowKey={(r) => r.user_id}
              onRowClick={(r) => navigate(`/reports/user/${r.user_id}`)}
              defaultSort={{ key: "leads", dir: "desc" }}
            />
          </SectionCard>
        )}
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Lost analysis
// ---------------------------------------------------------------------------

export function LostTab({ range, headId, userId, extra }: TabProps) {
  const { data, loading, error } = useReport<{
    lead_reasons: Array<{ reason: string; count: number }>;
    deal_reasons: Array<{ reason: string; count: number; amount: number }>;
  }>(`/api/Report/LostAnalysis${q(range, headId, userId, extra || {})}`);
  if (!data && !error) return <ReportSkeleton tiles={4} cards={2} />;
  const deals = data?.deal_reasons || [];
  const leads = data?.lead_reasons || [];
  const lostDeals = deals.reduce((t, r) => t + r.count, 0);
  const lostValue = deals.reduce((t, r) => t + r.amount, 0);
  const disqualified = leads.reduce((t, r) => t + r.count, 0);
  const top = [...deals].sort((a, b) => b.amount - a.amount)[0];
  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
          <StatTile label="Deals lost" value={fmtNum(lostDeals)} tone={lostDeals ? "red" : "green"} icon={<Ban />} hint={<span>marked lost in the period</span>} />
          <StatTile label="Value lost" value={fmtMoney(lostValue)} tone={lostValue ? "red" : "green"} icon={<IndianRupee />} />
          <StatTile label="Leads disqualified" value={fmtNum(disqualified)} tone={disqualified ? "orange" : "green"} icon={<UserX />} />
          <StatTile label="Biggest reason" value={top?.reason || "—"} tone="purple" icon={<ListChecks />} hint={top ? <span>{fmtMoney(top.amount)} across {top.count} deal{top.count === 1 ? "" : "s"}</span> : <span>nothing lost</span>} />
        </div>
        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-2">
          <SectionCard title="Why deals were lost" description="Deals marked lost in the period, by value" icon={<Ban />} tone="red">
            <BarList tone="red" data={deals.map((r) => ({ label: r.reason, value: r.amount, detail: [`${r.count} deals`] }))} format={(v) => fmtMoney(v)} emptyMessage="No deals lost in this period." />
          </SectionCard>
          <SectionCard title="Why leads were disqualified" description="Leads disqualified in the period" icon={<UserX />} tone="orange">
            <BarList tone="orange" data={leads.map((r) => ({ label: r.reason, value: r.count }))} emptyMessage="No leads disqualified in this period." />
          </SectionCard>
        </div>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Lead ageing
// ---------------------------------------------------------------------------

/** Idle-time buckets: fresh is green, then blue, purple, orange, and 30+ days red */
const IDLE_BUCKETS: { key: "d0_3" | "d4_7" | "d8_14" | "d15_30" | "d30_plus"; label: string; tone: Tone }[] = [
  { key: "d0_3", label: "0–3 days", tone: "green" },
  { key: "d4_7", label: "4–7 days", tone: "blue" },
  { key: "d8_14", label: "8–14 days", tone: "purple" },
  { key: "d15_30", label: "15–30 days", tone: "orange" },
  { key: "d30_plus", label: "30+ days", tone: "red" },
];
const PRIORITY_TONE: Record<string, Tone> = { HOT: "red", WARM: "orange", COLD: "blue" };

type AgeingRow = { lead_status: string; d0_3: number; d4_7: number; d8_14: number; d15_30: number; d30_plus: number; total: number };
type AgeingOwner = { user_id: string; name: string; total: number; fresh: number; ageing: number; d30_plus: number; stuck: number; avg_idle_days: number };

export function AgeingTab({ headId, userId, extra, days, onDays }: TabProps & { days: number; onDays: (d: number) => void }) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<{
    rows: AgeingRow[];
    total_stuck: number;
    by_owner: AgeingOwner[];
    stuck_leads: Array<{ id: string; name: string; company_name: string; lead_status: string; priority: string; owner_name: string; last_activity_at: string | null; idle_days: number }>;
  }>(`/api/Report/LeadAgeing${buildQuery({ head_id: headId, user_id: userId, days, ...(extra || {}) })}`);
  if (!data && !error) return <ReportSkeleton tiles={5} cards={2} />;

  const rows = data?.rows || [];
  const sum = (k: keyof AgeingRow) => rows.reduce((t, r) => t + (Number(r[k]) || 0), 0);
  const open = sum("total");
  const active = sum("d0_3") + sum("d4_7");
  const share = (n: number) => (open ? `${Math.round((n / open) * 100)}% of open leads` : "no open leads");
  const maxRow = Math.max(0, ...rows.map((r) => r.total));
  const owners = data?.by_owner || [];
  const maxOwner = Math.max(0, ...owners.map((o) => o.total));
  const stuck = data?.stuck_leads || [];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-5">
          <StatTile label="Open leads" value={fmtNum(open)} tone="blue" icon={<Users />} hint={<span>as of now</span>} />
          <StatTile label="Active this week" value={fmtNum(active)} tone="green" icon={<CheckCircle2 />} hint={<span>{share(active)}</span>} />
          <StatTile label="Idle 8–30 days" value={fmtNum(sum("d8_14") + sum("d15_30"))} tone={sum("d8_14") + sum("d15_30") ? "orange" : "green"} icon={<Hourglass />} hint={<span>{share(sum("d8_14") + sum("d15_30"))}</span>} />
          <StatTile label="Idle 30+ days" value={fmtNum(sum("d30_plus"))} tone={sum("d30_plus") ? "red" : "green"} icon={<TriangleAlert />} hint={<span>{share(sum("d30_plus"))}</span>} />
          <StatTile label={`Stuck (${days}+ days)`} value={fmtNum(data?.total_stuck || 0)} tone={data?.total_stuck ? "red" : "green"} icon={<Clock />} hint={<span>no activity for more than {days} days</span>} />
        </div>

        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-2">
          <SectionCard title="Idle time by status" description="Days since the last activity on each open lead" icon={<Hourglass />} tone="orange">
            {open === 0 ? (
              <EmptyState message="No open leads for these filters." />
            ) : (
              <div className="space-y-2.5">
                {rows.map((r) => (
                  <StackedBar key={r.lead_status} label={LEAD_STATUS_LABEL[r.lead_status] || r.lead_status} max={maxRow} total={r.total} parts={IDLE_BUCKETS.map((b) => ({ label: b.label, value: r[b.key], tone: b.tone }))} />
                ))}
                <StackLegend parts={IDLE_BUCKETS} />
              </div>
            )}
          </SectionCard>
          {owners.length > 1 ? (
            <SectionCard title="By salesperson" description="Open leads by idle time; click a row for that person's report" icon={<Users />} tone="purple">
              <div className="space-y-2.5">
                {owners.map((o) => (
                  <StackedBar
                    key={o.user_id}
                    label={o.name}
                    max={maxOwner}
                    total={o.total}
                    onClick={() => navigate(`/reports/user/${o.user_id}`)}
                    parts={[
                      { label: "Active (≤7 days)", value: o.fresh, tone: "green" },
                      { label: "Idle 8–30 days", value: o.ageing, tone: "orange" },
                      { label: "Idle 30+ days", value: o.d30_plus, tone: "red" },
                    ]}
                  />
                ))}
                <StackLegend parts={[{ label: "Active (≤7 days)", tone: "green" }, { label: "Idle 8–30 days", tone: "orange" }, { label: "Idle 30+ days", tone: "red" }]} />
              </div>
            </SectionCard>
          ) : (
            <SectionCard title="Open leads by idle time" description="Counts behind the chart" icon={<Table2 />}>
              <AgeingTable rows={rows} />
            </SectionCard>
          )}
        </div>

        {owners.length > 1 && (
          <SectionCard title="Open leads by idle time" description="Counts behind the chart" icon={<Table2 />}>
            <AgeingTable rows={rows} />
          </SectionCard>
        )}

        <SectionCard
          title="Stuck leads"
          description={
            (data?.total_stuck || 0) > stuck.length
              ? `Open leads with no activity for more than ${days} days — the ${stuck.length} longest idle of ${fmtNum(data!.total_stuck)}. The Excel file lists them all.`
              : `Open leads with no activity for more than ${days} days`
          }
          icon={<TriangleAlert />}
          tone="red"
          action={
            <SearchableSelect aria-label="Stuck after" className={cn(filterSelectClass, "w-28 shrink-0")} value={days} onChange={(e) => onDays(Number(e.target.value))}>
              {[3, 7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d}+ days
                </option>
              ))}
            </SearchableSelect>
          }
        >
          <DataTable
            columns={[
              { key: "name", header: "Lead", render: (r) => <Link to={`/leads/${r.id}`} className="font-medium text-primary hover:underline">{r.name || r.company_name}</Link> },
              { key: "company_name", header: "Company", render: (r) => r.company_name || "—" },
              { key: "lead_status", header: "Status", render: (r) => LEAD_STATUS_LABEL[r.lead_status] || r.lead_status },
              { key: "priority", header: "Priority", render: (r) => (r.priority ? <StatusPill tone={PRIORITY_TONE[r.priority] || "gray"}>{r.priority.charAt(0) + r.priority.slice(1).toLowerCase()}</StatusPill> : "—") },
              { key: "owner_name", header: "Owner" },
              { key: "last_activity_at", header: "Last activity", sortValue: (r) => r.last_activity_at || "", render: (r) => fmtDate(r.last_activity_at) },
              { key: "idle_days", header: "Idle days", align: "right", render: (r) => <span className={r.idle_days > 30 ? "font-semibold text-danger" : "font-semibold text-warning"}>{r.idle_days}</span> },
            ]}
            rows={stuck}
            rowKey={(r) => r.id}
            defaultSort={{ key: "idle_days", dir: "desc" }}
            emptyMessage={`No open leads have been idle for more than ${days} days.`}
          />
        </SectionCard>
      </div>
    </Status>
  );
}

function AgeingTable({ rows }: { rows: AgeingRow[] }) {
  return (
    <DataTable
      columns={[
        { key: "lead_status", header: "Status", render: (r) => <span className="font-medium text-foreground">{LEAD_STATUS_LABEL[r.lead_status] || r.lead_status}</span> },
        ...IDLE_BUCKETS.map((b) => ({ key: b.key, header: b.label, align: "right" as const, render: (r: AgeingRow) => (r[b.key] && (b.tone === "red" || b.tone === "orange") ? <span className={cn("font-semibold", TONE[b.tone].text)}>{r[b.key]}</span> : r[b.key]) })),
        { key: "total", header: "Total", align: "right", render: (r) => <span className="font-semibold text-foreground">{r.total}</span> },
      ]}
      rows={rows}
      rowKey={(r) => r.lead_status}
      emptyMessage="No open leads for these filters."
    />
  );
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

type ActivityRow = { user_id: string; name: string; role: string; calls: number; meetings: number; emails: number; whatsapp: number; notes: number; visits: number; visit_avg_minutes: number; visits_flagged: number; total_activities: number; tasks_done: number; tasks_overdue: number };
const ACTIVITY_KINDS: { key: "calls" | "meetings" | "emails" | "whatsapp" | "notes" | "visits"; label: string; tone: Tone }[] = [
  { key: "calls", label: "Calls", tone: "blue" },
  { key: "meetings", label: "Meetings", tone: "purple" },
  { key: "emails", label: "Emails", tone: "green" },
  { key: "whatsapp", label: "WhatsApp", tone: "orange" },
  { key: "notes", label: "Notes", tone: "gray" },
  { key: "visits", label: "Visits", tone: "red" },
];

export function ActivityTab({ range, headId, userId }: TabProps) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<{ rows: ActivityRow[]; daily: Array<{ day: string; activities: number }> }>(`/api/Report/Activity${q(range, headId, userId)}`);
  if (!data && !error) return <ReportSkeleton tiles={8} cards={2} />;
  const rows = data?.rows || [];
  const daily = data?.daily || [];
  const t = (k: keyof ActivityRow) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const maxRow = Math.max(0, ...rows.map((r) => r.total_activities));
  const busiest = [...daily].sort((a, b) => b.activities - a.activities)[0];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
          <StatTile label="All activities" value={fmtNum(t("total_activities"))} tone="blue" icon={<ListChecks />} hint={<span>{busiest ? `busiest day ${fmtDate(busiest.day)}` : "nothing logged"}</span>} />
          <StatTile label="Calls" value={fmtNum(t("calls"))} tone="blue" icon={<Phone />} hint={<span>logged on deals</span>} />
          <StatTile label="Meetings" value={fmtNum(t("meetings"))} tone="purple" icon={<CalendarDays />} />
          <StatTile label="Emails and WhatsApp" value={fmtNum(t("emails") + t("whatsapp"))} tone="blue" icon={<Send />} hint={<span>{fmtNum(t("emails"))} emails · {fmtNum(t("whatsapp"))} WhatsApp</span>} />
          <StatTile label="Field visits" value={fmtNum(t("visits"))} tone={t("visits_flagged") ? "orange" : "blue"} icon={<MapPin />} hint={<span>{t("visits_flagged") ? `${fmtNum(t("visits_flagged"))} flagged for location` : "none flagged"}</span>} />
          <StatTile label="Notes" value={fmtNum(t("notes"))} tone="gray" icon={<FileText />} />
          <StatTile label="Tasks done" value={fmtNum(t("tasks_done"))} tone="green" icon={<CheckCircle2 />} hint={<span>in the period</span>} />
          <StatTile label="Overdue tasks" value={fmtNum(t("tasks_overdue"))} tone={t("tasks_overdue") ? "red" : "green"} icon={<TriangleAlert />} hint={<span>right now</span>} />
        </div>

        <SectionCard title="Activities per day" description="Calls, meetings, emails, WhatsApp and notes logged on deals" icon={<BarChart3 />}>
          <ColumnChart
            data={daily.map((d) => ({ label: new Date(`${d.day}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" }), value: d.activities }))}
            emptyMessage="No activities logged in this period."
          />
        </SectionCard>

        {rows.length > 1 && (
          <SectionCard title="Activity mix by person" description="Click a row for that person's report" icon={<Users />} tone="purple">
            {maxRow === 0 ? (
              <EmptyState message="No activities logged in this period." />
            ) : (
              <div className="space-y-2.5">
                {[...rows].sort((a, b) => b.total_activities - a.total_activities).map((r) => (
                  <StackedBar key={r.user_id} label={r.name} max={maxRow} total={r.total_activities} onClick={() => navigate(`/reports/user/${r.user_id}`)} parts={ACTIVITY_KINDS.map((k) => ({ label: k.label, value: r[k.key], tone: k.tone }))} />
                ))}
                <StackLegend parts={ACTIVITY_KINDS} />
              </div>
            )}
          </SectionCard>
        )}

        <SectionCard title="Activity by person" description="Overdue tasks are as of now; everything else follows the period" icon={<Table2 />}>
          <DataTable
            columns={[
              { key: "name", header: "Name", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
              { key: "role", header: "Role" },
              { key: "calls", header: "Calls", align: "right" },
              { key: "meetings", header: "Meetings", align: "right" },
              { key: "emails", header: "Emails", align: "right" },
              { key: "whatsapp", header: "WhatsApp", align: "right" },
              { key: "notes", header: "Notes", align: "right" },
              { key: "visits", header: "Visits", align: "right" },
              { key: "visit_avg_minutes", header: "Avg on site", align: "right", render: (r) => (r.visits ? `${r.visit_avg_minutes} min` : "—") },
              { key: "visits_flagged", header: "Flagged visits", align: "right", render: (r) => <Flag n={r.visits_flagged} /> },
              { key: "total_activities", header: "Total", align: "right", render: (r) => <span className="font-semibold text-foreground">{r.total_activities}</span> },
              { key: "tasks_done", header: "Tasks done", align: "right", render: (r) => <span className="text-success">{r.tasks_done}</span> },
              { key: "tasks_overdue", header: "Overdue now", align: "right", render: (r) => <Flag n={r.tasks_overdue} tone="red" /> },
            ]}
            rows={rows}
            rowKey={(r) => r.user_id}
            defaultSort={{ key: "total_activities", dir: "desc" }}
            emptyMessage="Nobody to show for these filters."
            footer={
              rows.length > 1 ? (
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold tabular-nums">
                    <td className="px-2 py-2">Total</td>
                    <td className="px-2 py-2" />
                    {(["calls", "meetings", "emails", "whatsapp", "notes", "visits"] as const).map((k) => (
                      <td key={k} className="px-2 py-2 text-right">{fmtNum(t(k))}</td>
                    ))}
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right">{fmtNum(t("visits_flagged"))}</td>
                    <td className="px-2 py-2 text-right">{fmtNum(t("total_activities"))}</td>
                    <td className="px-2 py-2 text-right">{fmtNum(t("tasks_done"))}</td>
                    <td className="px-2 py-2 text-right">{fmtNum(t("tasks_overdue"))}</td>
                  </tr>
                </tfoot>
              ) : undefined
            }
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

type QuoteRow = { user_id: string; name: string; role: string; created: number; submitted: number; approved: number; sent: number; accepted: number; rejected: number; pending_now: number; acceptance_pct: number; accepted_value: number; avg_discount_pct: number; avg_approval_hours: number };

export function QuotesTab({ range, headId, userId, extra }: TabProps) {
  const { data, loading, error } = useReport<{ rows: QuoteRow[] }>(`/api/Report/Quotes${q(range, headId, userId, extra || {})}`);
  if (!data && !error) return <ReportSkeleton tiles={4} cards={1} />;
  const rows = data?.rows || [];
  const t = (k: keyof QuoteRow) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const acceptance = t("sent") ? Math.round((t("accepted") / t("sent")) * 1000) / 10 : null;
  // Weighted by how many quotes each person's average covers
  const discount = t("submitted") ? Math.round((rows.reduce((s, r) => s + r.avg_discount_pct * r.submitted, 0) / t("submitted")) * 10) / 10 : null;
  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-5">
          <StatTile label="Quotes created" value={fmtNum(t("created"))} tone="blue" icon={<FileText />} hint={<span>{fmtNum(t("submitted"))} submitted for approval</span>} />
          <StatTile label="Sent" value={fmtNum(t("sent"))} tone="blue" icon={<Send />} hint={<span>to customers</span>} />
          <StatTile label="Accepted" value={fmtNum(t("accepted"))} tone="green" icon={<CheckCircle2 />} hint={<span>{acceptance === null ? "nothing sent" : `${fmtPct(acceptance)} of quotes sent`} · {fmtMoney(t("accepted_value"))}</span>} />
          <StatTile label="Waiting for approval" value={fmtNum(t("pending_now"))} tone={t("pending_now") ? "orange" : "green"} icon={<Clock />} hint={<span>right now · {fmtNum(t("rejected"))} rejected</span>} />
          <StatTile label="Average discount" value={fmtPct(discount)} tone="purple" icon={<Percent />} hint={<span>on quotes submitted</span>} />
        </div>
        {rows.length > 1 && (
          <SectionCard title="Accepted value by owner" icon={<Trophy />} tone="green">
            <BarList tone="green" data={rows.filter((r) => r.accepted_value > 0).map((r) => ({ label: r.name, value: r.accepted_value, detail: [`${r.accepted} accepted`] }))} format={(v) => fmtMoney(v)} emptyMessage="No quotes accepted in this period." />
          </SectionCard>
        )}
        <SectionCard title="Quotes by owner" description="Quote funnel, discounts and approval time in the period" icon={<Users />}>
          <DataTable
            columns={[
              { key: "name", header: "Owner", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
              { key: "created", header: "Created", align: "right" },
              { key: "submitted", header: "Submitted", align: "right" },
              { key: "approved", header: "Approved", align: "right" },
              { key: "sent", header: "Sent", align: "right" },
              { key: "accepted", header: "Accepted", align: "right", render: (r) => <span className="text-success">{r.accepted}</span> },
              { key: "rejected", header: "Rejected", align: "right", render: (r) => <span className={r.rejected ? "text-danger" : ""}>{r.rejected}</span> },
              { key: "acceptance_pct", header: "Accept %", align: "right", render: (r) => fmtPct(r.acceptance_pct) },
              { key: "accepted_value", header: "Accepted value", align: "right", render: (r) => <span className="font-semibold">{fmtMoney(r.accepted_value)}</span> },
              { key: "avg_discount_pct", header: "Avg disc.", align: "right", render: (r) => fmtPct(r.avg_discount_pct) },
              { key: "avg_approval_hours", header: "Avg approval (h)", align: "right" },
              { key: "pending_now", header: "Pending now", align: "right", render: (r) => (r.pending_now ? <StatusPill tone="orange">{r.pending_now}</StatusPill> : <span className="text-muted-foreground">0</span>) },
            ]}
            rows={rows}
            rowKey={(r) => r.user_id}
            defaultSort={{ key: "accepted_value", dir: "desc" }}
            emptyMessage="No quote activity for these filters."
            footer={
              rows.length > 1 ? (
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold tabular-nums">
                    <td className="px-2 py-2">Total</td>
                    {(["created", "submitted", "approved", "sent", "accepted", "rejected"] as (keyof QuoteRow)[]).map((k) => (
                      <td key={k} className="px-2 py-2 text-right">{fmtNum(t(k))}</td>
                    ))}
                    <td className="px-2 py-2 text-right">{fmtPct(acceptance)}</td>
                    <td className="px-2 py-2 text-right">{fmtMoney(t("accepted_value"))}</td>
                    <td className="px-2 py-2 text-right">{fmtPct(discount)}</td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right">{fmtNum(t("pending_now"))}</td>
                  </tr>
                </tfoot>
              ) : undefined
            }
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Forecast
// ---------------------------------------------------------------------------

export function ForecastTab({ headId, userId, extra }: TabProps) {
  const { data, loading, error } = useReport<{
    overdue: ForecastBucket;
    this_month: ForecastBucket;
    next_month: ForecastBucket;
    this_quarter: ForecastBucket;
    undated_deals: number;
    by_owner: Array<{ user_id: string; name: string; deals: number; amount: number; weighted: number }>;
    closing_deals: Array<{ id: string; name: string; amount: number; weighted_amount: number; probability: number; expected_close_date: string; stage_name: string; owner_name: string }>;
  }>(`/api/Report/Forecast${buildQuery({ head_id: headId, user_id: userId, ...(extra || {}) })}`);
  if (!data && !error) return <ReportSkeleton tiles={4} cards={2} />;

  const tile = (label: string, b: ForecastBucket | undefined, tone: Tone, icon: React.ReactNode) => (
    <StatTile label={label} value={fmtMoney(b?.weighted || 0)} tone={tone} icon={icon} hint={<span>{b?.deals || 0} deals · {fmtMoney(b?.amount || 0)} total</span>} />
  );
  const today = new Date(new Date().toDateString());

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
          {tile("This month (weighted)", data?.this_month, "blue", <CalendarDays />)}
          {tile("Next month (weighted)", data?.next_month, "purple", <CalendarClock />)}
          {tile("This quarter (weighted)", data?.this_quarter, "green", <CalendarRange />)}
          {tile("Past expected date", data?.overdue, data?.overdue.deals ? "red" : "gray", <TriangleAlert />)}
        </div>
        {!!data?.undated_deals && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <StatusPill tone="orange">{data.undated_deals} undated</StatusPill>
            open deals have no expected close date and are not in the forecast.
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-3">
          <SectionCard title="This month by owner" description="Weighted value" icon={<Users />} tone="purple">
            <BarList tone="purple" data={(data?.by_owner || []).map((o) => ({ label: o.name, value: o.weighted, detail: [`${o.deals} deals · ${fmtMoney(o.amount)}`] }))} format={(v) => fmtMoney(v)} emptyMessage="Nothing expected to close this month." />
          </SectionCard>
          <SectionCard title="Deals closing this month" description="Including deals past their expected date" className="@4xl/main:col-span-2" icon={<CalendarDays />}>
            <DataTable
              columns={[
                { key: "name", header: "Deal", render: (r) => <Link to={`/deals/${r.id}`} className="font-medium text-primary hover:underline">{r.name}</Link> },
                { key: "owner_name", header: "Owner" },
                { key: "stage_name", header: "Stage" },
                {
                  key: "expected_close_date",
                  header: "Expected",
                  render: (r) => (new Date(r.expected_close_date) < today ? <StatusPill tone="red">{fmtDate(r.expected_close_date)}</StatusPill> : fmtDate(r.expected_close_date)),
                },
                { key: "probability", header: "Prob.", align: "right", render: (r) => <span className={r.probability >= 70 ? "font-semibold text-success" : ""}>{fmtPct(r.probability)}</span> },
                { key: "amount", header: "Amount", align: "right", render: (r) => fmtMoney(r.amount) },
              ]}
              rows={data?.closing_deals || []}
              rowKey={(r) => r.id}
              emptyMessage="Nothing expected to close this month."
            />
          </SectionCard>
        </div>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Product sales
// ---------------------------------------------------------------------------

export function ProductsTab({ range, headId, userId, extra }: TabProps) {
  const { data, loading, error } = useReport<{
    rows: Array<{ product_id: string; product_name: string; product_code: string; deals: number; quantity: number; revenue: number; cost?: number; margin?: number; margin_pct?: number }>;
  }>(`/api/Report/ProductSales${q(range, headId, userId, extra || {})}`);
  if (!data && !error) return <ReportSkeleton tiles={4} cards={1} />;
  const rows = data?.rows || [];
  // cost / margin are removed by the server for users without cost visibility
  const hasCost = rows.some((r) => r.margin !== undefined);
  const revenue = rows.reduce((t, r) => t + r.revenue, 0);
  const margin = rows.reduce((t, r) => t + (r.margin || 0), 0);
  const marginPct = revenue ? Math.round((margin / revenue) * 1000) / 10 : null;

  const columns: Column<(typeof rows)[number]>[] = [
    { key: "product_name", header: "Product", render: (r) => <span className="font-medium text-foreground">{r.product_name}</span> },
    { key: "product_code", header: "Code", render: (r) => r.product_code || "—" },
    { key: "deals", header: "Deals", align: "right" },
    { key: "quantity", header: "Qty", align: "right", render: (r) => fmtNum(r.quantity) },
    { key: "revenue", header: "Revenue", align: "right", render: (r) => <span className="font-semibold">{fmtMoney(r.revenue)}</span> },
    ...(hasCost
      ? ([
          { key: "cost", header: "Cost", align: "right", render: (r) => fmtMoney(r.cost || 0) },
          { key: "margin", header: "Margin", align: "right", render: (r) => <span className={(r.margin || 0) < 0 ? "text-danger" : "text-success"}>{fmtMoney(r.margin || 0)}</span> },
          { key: "margin_pct", header: "Margin %", align: "right", render: (r) => <span className={(r.margin_pct || 0) < 10 ? "font-semibold text-warning" : ""}>{fmtPct(r.margin_pct || 0)}</span> },
        ] as Column<(typeof rows)[number]>[])
      : []),
  ];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
          <StatTile label="Product revenue" value={fmtMoney(revenue)} tone="green" icon={<IndianRupee />} hint={<span>line totals of won deals</span>} />
          <StatTile label="Units sold" value={fmtNum(rows.reduce((t, r) => t + r.quantity, 0))} tone="blue" icon={<Boxes />} />
          <StatTile label="Products sold" value={fmtNum(rows.length)} tone="purple" icon={<Package />} hint={<span>{rows[0] ? `top: ${rows[0].product_name}` : "none"}</span>} />
          {hasCost ? (
            <StatTile label="Margin" value={fmtMoney(margin)} tone={margin < 0 ? "red" : "green"} icon={<Percent />} hint={<span>{fmtPct(marginPct)} of revenue</span>} />
          ) : (
            <StatTile label="Average per deal line" value={fmtMoney(rows.length ? revenue / Math.max(1, rows.reduce((t, r) => t + r.deals, 0)) : 0)} tone="gray" icon={<Percent />} />
          )}
        </div>
        <SectionCard title="Revenue by product" description="Line totals from deals won in the period (top 15)" icon={<BarChart3 />} tone="green">
          <BarList tone="green" data={rows.slice(0, 15).map((r) => ({ label: r.product_name, value: r.revenue, detail: [`${fmtNum(r.quantity)} units`] }))} format={(v) => fmtMoney(v)} emptyMessage="No won deals with products for these filters." />
        </SectionCard>
        <SectionCard title="Product details" icon={<Table2 />}>
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.product_id || r.product_name} defaultSort={{ key: "revenue", dir: "desc" }} emptyMessage="No won deals with products for these filters." />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Assignment audit
// ---------------------------------------------------------------------------

const ASSIGN_TYPE: Record<string, { label: string; tone: Tone }> = {
  AUTO: { label: "Automatic", tone: "blue" },
  MANUAL: { label: "By a person", tone: "purple" },
  POOL: { label: "From the pool", tone: "orange" },
  IMPORT: { label: "Import", tone: "gray" },
};
/** A saved reason code in words; text a person typed stays as typed */
const reasonText = (r: string) => (/^[A-Z0-9_:]+$/.test(r || "") ? r.replace(/[_:]+/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : r || "—");

export function AssignmentsTab({ range, headId, userId, extra }: TabProps) {
  const navigate = useNavigate();
  const rangeKey = `${range.from}|${range.to}|${headId}|${userId}|${extra?.assign_type || ""}`;
  const [pageState, setPageState] = useState({ key: rangeKey, page: 1 });
  const page = pageState.key === rangeKey ? pageState.page : 1;
  const { data, loading, error } = useReport<{
    rows: Array<{ id: string; created_on: string; reason: string; assign_type: string; lead_id: string; lead_name: string; company_name: string; from_user: string; to_user: string; assigned_by: string }>;
    total: number;
    page: number;
    page_size: number;
    by_type: Array<{ key: string; count: number }>;
    by_receiver: Array<{ user_id: string; name: string; received: number; auto: number; manual: number; pool: number; import: number; reassigned: number }>;
  }>(`/api/Report/AssignmentAudit${q(range, headId, userId, { ...(extra || {}), page, page_size: 30 })}`);
  if (!data && !error) return <ReportSkeleton tiles={5} cards={2} />;
  const rows = data?.rows || [];
  const total = data?.total ?? rows.length;
  const pageSize = data?.page_size || 30;
  const count = (k: string) => data?.by_type?.find((x) => x.key === k)?.count || 0;
  const receivers = data?.by_receiver || [];
  const moved = receivers.reduce((t, r) => t + r.reassigned, 0);

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-5">
          <StatTile label="Assignments" value={fmtNum(total)} tone="blue" icon={<Inbox />} hint={<span>in the period</span>} />
          <StatTile label="Automatic" value={fmtNum(count("AUTO"))} tone="blue" icon={<Shuffle />} hint={<span>round robin and lead intake</span>} />
          <StatTile label="By a person" value={fmtNum(count("MANUAL"))} tone="purple" icon={<UserCheck />} hint={<span>assigned or reassigned by hand</span>} />
          <StatTile label="From the pool" value={fmtNum(count("POOL"))} tone={count("POOL") ? "orange" : "gray"} icon={<Layers />} hint={<span>claimed or left in the pool{count("IMPORT") ? ` · ${fmtNum(count("IMPORT"))} imported` : ""}</span>} />
          <StatTile label="Moved from someone" value={fmtNum(moved)} tone={moved ? "orange" : "green"} icon={<ArrowRightLeft />} hint={<span>leads that had an owner before</span>} />
        </div>

        <div className="grid grid-cols-1 gap-4 @4xl/main:grid-cols-3">
          <SectionCard title="How leads were assigned" icon={<Shuffle />} tone="purple">
            <BarList tone="purple" data={(data?.by_type || []).map((t) => ({ label: ASSIGN_TYPE[t.key]?.label || t.key, value: t.count }))} emptyMessage="No assignments in this period." />
          </SectionCard>
          <SectionCard title="Leads received by salesperson" description="Click a row for that person's report" icon={<Users />} className="@4xl/main:col-span-2">
            <DataTable
              columns={[
                { key: "name", header: "Salesperson", render: (r) => <span className="font-medium text-foreground">{r.name || "—"}</span> },
                { key: "received", header: "Received", align: "right", render: (r) => <span className="font-semibold">{r.received}</span> },
                { key: "auto", header: "Automatic", align: "right" },
                { key: "manual", header: "By a person", align: "right" },
                { key: "pool", header: "From the pool", align: "right" },
                { key: "import", header: "Import", align: "right" },
                { key: "reassigned", header: "Moved from someone", align: "right", render: (r) => <Flag n={r.reassigned} /> },
              ]}
              rows={receivers}
              rowKey={(r) => r.user_id}
              onRowClick={(r) => navigate(`/reports/user/${r.user_id}`)}
              defaultSort={{ key: "received", dir: "desc" }}
              emptyMessage="Nobody received leads in this period."
            />
          </SectionCard>
        </div>

        <SectionCard title="Lead assignments" description="Auto-assignments, reassignments and pool claims, newest first" icon={<Table2 />}>
          {rows.length === 0 ? (
            <EmptyState message="No assignments for these filters." />
          ) : (
            <>
              <DataTable
                columns={[
                  { key: "created_on", header: "When", sortValue: (r) => r.created_on, render: (r) => fmtDate(r.created_on) },
                  { key: "lead_name", header: "Lead", render: (r) => <Link to={`/leads/${r.lead_id}`} className="font-medium text-primary hover:underline">{r.lead_name || r.company_name}</Link> },
                  { key: "assign_type", header: "Type", render: (r) => <StatusPill tone={ASSIGN_TYPE[r.assign_type]?.tone || "gray"}>{ASSIGN_TYPE[r.assign_type]?.label || r.assign_type}</StatusPill> },
                  { key: "from_user", header: "From", render: (r) => r.from_user || <span className="text-muted-foreground">Pool / new</span> },
                  { key: "to_user", header: "To", render: (r) => r.to_user || "—" },
                  { key: "assigned_by", header: "By", render: (r) => r.assigned_by || "—" },
                  { key: "reason", header: "Reason", render: (r) => reasonText(r.reason) },
                ]}
                rows={rows}
                rowKey={(r) => r.id}
                defaultSort={{ key: "created_on", dir: "desc" }}
              />
              <ListPager page={page} pageSize={pageSize} total={total} onPage={(next) => setPageState({ key: rangeKey, page: next })} disabled={loading} />
            </>
          )}
        </SectionCard>
      </div>
    </Status>
  );
}
